/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useOutletContext,
  useNavigate
} from 'react-router-dom'
import { Loading, ControlButtons } from '@renderer/components'
import ScaleToFit from '@renderer/components/ScaleToFit'
import { SettingsModal, SettingsModalLegacy } from '@renderer/components/view'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import {
  usePrintStore,
  type PrintSettings,
  type RemotePrinterEntry
} from '@renderer/stores/printStore'
import { PrintUtils } from '@renderer/utils/printUtils'
import { describePrinterMedia } from '@renderer/utils/printerMedia'
import cogoToast from '@successtar/cogo-toast'
import { getBrandLogoPath, getBrandProductName } from '@renderer/utils/brandUtils'
import { getCustomRoutes } from './view/customRoutes'
import PostSessionAIPage from './view/PostSessionAI'
import { useHubStore } from '@renderer/stores/hubStore'
import { getActiveHubMembers, getHomePath } from '@renderer/utils/navigationUtils'
import { MULTI_PROJECT_HUB_FLAG } from '@renderer/utils/hubSupport'
import { prefetchHubMembers } from '@renderer/utils/projectConfigCache'
import { useHomePath } from '@renderer/hooks/useHomePath'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { cameraLog } from '@renderer/utils/cameras/cameraLog'
import {
  resolveConnectArgs,
  findUsbCameraBySelectionLoose,
  isDualCameraEnabled
} from '@renderer/utils/cameras/cameraSelection'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import type { SettingsGroupId } from '@renderer/components/view/SettingsNav'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { CameraPrompt } from '@renderer/components/view/CameraPrompt'
import { FaExternalLinkAlt, FaTimes } from 'react-icons/fa'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useTranslation } from 'react-i18next'
import { UpgradeModal } from '@renderer/components/UpgradeModal'
import { useUploadStore } from '@renderer/stores/uploadStore'
import { createContext, useContext } from 'react'
import { useDeviceManager } from '@renderer/hooks'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import { useResolvedProjectId } from '@renderer/hooks/useResolvedProjectId'
import { useRouteBasePath } from '@renderer/hooks/useRouteBasePath'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'
import { ResponsiveTierProvider } from '@renderer/context/ResponsiveTierContext'
import type { DesignOrientation } from '@renderer/types/configState'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'
import { CameraWarningModal } from '@renderer/components/view/CameraWarningModal'
import { Screensaver } from '@renderer/components/Screensaver'
import { useIdleStore } from '@renderer/stores/idleStore'
import {
  CAMERA_REST_FLAG,
  useCameraRestStore,
  hasAnyPreviewConsumers
} from '@renderer/stores/cameraRestStore'
import {
  openExternalUrl,
  ensureCameraPermission,
  getTunnelStatus,
  hasElectronPreload,
  isDigitalMode,
  setIdleState,
  reportFlowActive,
} from '@renderer/lib/runtime'
import { watchTunnelStatus } from '@renderer/lib/runtime/tunnelStatusWatch'
import { connectDigitalCamera } from '@renderer/lib/digitalCamera'
import {
  getStoredProjectEffectiveUpdatedAt,
  setStoredProjectEffectiveUpdatedAt
} from '@renderer/utils/projectVersionCache'
import { isClickLogSuppressed } from '@renderer/utils/clickLog'

/**
 * True when the booth may operate: valid device subscription, or a positive credit balance.
 * Stale "Unlimited" with invalid sub must not count as access (avoids Number('Unlimited') => NaN).
 */
function hasUsableBoothAccess(
  isSubscriptionValid: boolean,
  credits: number | string
): boolean {
  if (isSubscriptionValid) return true
  if (typeof credits === 'number') return credits > 0
  if (credits === 'Unlimited') return false
  const n = Number(credits)
  if (Number.isNaN(n)) return false
  return n > 0
}

// Context for camera recovery
export interface CameraRecoveryContextType {
  onCameraRecoveryRequest: (onReconnected: () => void) => void
}

export const CameraRecoveryContext = createContext<CameraRecoveryContextType | null>(null)

export const useCameraRecovery = (): CameraRecoveryContextType | null => {
  return useContext(CameraRecoveryContext)
}

const NoCreditsModal: React.FC<{
  onClose: () => void
  setShowUpgradeModal: (show: boolean) => void
}> = ({ onClose, setShowUpgradeModal }) => {
  const { setDefaultProjectId } = useSettingsStore()
  const { t } = useTranslation()
  const isKlakKlikKluk = window.location.hostname.includes('klakklikkluk')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-4">{t('view.noCreditsModal.title')}</h2>
        <p className="text-gray-600 mb-6">{t('view.noCreditsModal.description')}</p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              setShowUpgradeModal(true)
            }}
            className="w-full flex justify-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            <span className="flex items-center gap-4">{t('projectNav.activateSubscription')}</span>
          </button>
          {!isKlakKlikKluk && (
            <button
              onClick={() => {
                setDefaultProjectId(undefined)
                void openExternalUrl(`${import.meta.env.VITE_API_URL}/dashboard/projects`)
              }}
              className="w-full flex justify-center bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
            >
              <span className="flex items-center gap-4">
                {t('view.noCreditsModal.topUpCredits')} <FaExternalLinkAlt />
              </span>
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full border border-gray-300 py-2 px-4 rounded hover:bg-gray-100 transition-colors"
          >
            {t('view.noCreditsModal.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

const PoweredByWatermark: React.FC<{ customPath: string }> = ({ customPath }) => {
  const show =
    customPath === 'booth-daughter' ||
    (customPath !== 'bahela' &&
      customPath !== 'bahelaevent' &&
      customPath !== 'bahela-quick' &&
      // Prefix, not equality: the karcis family is now two custom paths
      // (`bahelastruk` for the analog-TV booth, `bahelastruk-touch` for the
      // Cikstik iPad) and a third would be one PR away. An exact match meant
      // splitting the route silently re-enabled the Dreambooth watermark on a
      // whitelabel booth — invisible today only because that account happens
      // to sit on a tier the check below also exempts.
      !customPath.startsWith('bahelastruk') &&
      !window.location.hostname.includes('klakklikkluk') &&
      !['starter', 'protrial', 'professional', 'enterprise'].includes(
        useCreditsStore.getState().package
      ))
  if (!show) return null
  return (
    <div className="pointer-events-none absolute bottom-2 right-2 z-[90] flex origin-bottom-right items-center gap-1.5 opacity-70">
      <span className="text-xs text-gray-600">Powered By</span>
      <img src={getBrandLogoPath()} alt={getBrandProductName()} className="h-5" />
    </div>
  )
}

// Wrapper component to handle redirections
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const routeBasePath = useRouteBasePath()
  const location = useLocation()
  const { config, getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const currentPage = location.pathname.split('/').pop() || 'welcome'
  const { id: sessionId } = useSessionStateStore()
  // Hub members bounce to the HUB Welcome, not their own (multi-project hub).
  const homePath = useHomePath()
  // Booth-runtime gate for the post-session AI step (super-admin kill switch).
  // Default true so a missing flag (offline first boot) preserves the live feature;
  // the flag only ever hides the step, redirecting a direct visit back to Result.
  const aiEffectsEnabled = useFeatureFlag('ai_effects_app', true)

  // If no session ID exists and not on tutorial page, redirect to welcome page
  if (!sessionId && currentPage !== 'tutorial') {
    // This bounce is correct for cold deep-loads, but if it fires mid-flow it means
    // the session store was reset under a live guest (see cleanupSession's ownership
    // guard) — leave a breadcrumb so field reports aren't silent.
    console.warn(`[PAGE-WRAPPER] No session id on '${currentPage}' — redirecting to Welcome`)
    return <Navigate to={homePath} replace />
  }

  // Get the next page based on current page and settings
  const getNextPage = (): string | null => {
    const pages = config?.pages
    const { mode } = useSessionStateStore.getState()
    if (!pages) return null

    // Skip disabled pages for default /view and custom themes that share the
    // same route names (photophoria, bahela, …). Page components still own
    // Continue-button destinations; this only bounces off disabled steps.
    switch (currentPage) {
      case 'filter':
        if (!pages.filter?.settings?.enabled) {
          // With applyAfterCapture the Filter step sits AFTER capture, so bouncing to
          // 'frame' would throw the guest back into the pre-capture flow and re-shoot.
          return pages.filter?.settings?.applyAfterCapture ? 'select' : 'frame'
        }
        break
      case 'frame':
        if (!pages.frame?.settings?.enabled) {
          return welcomeSettings?.startWithPayment ? 'capture' : 'checkout'
        }
        break
      case 'checkout':
        if (!pages.checkout?.settings?.enabled) {
          return 'payment'
        }
        break
      case 'payment':
        if (!pages.payment?.settings?.enabled) {
          if (welcomeSettings?.startWithPayment) {
            const filterSettings = pages.filter?.settings
            const applyAfterCapture = filterSettings?.applyAfterCapture || false
            return applyAfterCapture ? 'frame' : 'filter'
          }
          return 'capture'
        }
        break
      case 'capture':
        if (
          pages.capture?.settings?.enabled === false ||
          pages.capture?.settings?.enabled === null
        ) {
          return 'retake'
        }
        break
      case 'retake':
        if (!pages.retake?.settings?.enabled) {
          const applyAfterCapture = pages.filter?.settings?.applyAfterCapture || false
          const filterEnabled = pages.filter?.settings?.enabled
          if (applyAfterCapture && filterEnabled) return 'filter'
          return 'select'
        }
        // Don't auto-redirect from retake page - let the page handle navigation via Continue button
        // The retake page will navigate based on frame-based mode and other settings
        break
      case 'record':
        // The record step is retired (page deleted). Any leftover navigation to
        // /record — stale welcome record buttons, legacy in-flight record-mode
        // sessions during the deploy window — forwards to where the flow continues.
        return mode === 'record' ? 'result' : 'select'
      case 'select':
        // Don't auto-redirect from select page - let the page handle navigation via Continue button
        // The select page will navigate to edit (if enabled) or result based on settings
        break
      case 'edit':
        // Check if Edit page is disabled - only redirect if disabled
        if (!pages.edit?.settings?.enabled) {
          return 'result'
        }
        // Don't auto-redirect from edit page - let the page handle navigation via Continue button
        // The edit page will navigate to result when user clicks Continue
        break
      case 'post-session-ai':
        if (!aiEffectsEnabled) {
          return 'result'
        }
        break
    }
    return null
  }

  const nextPage = getNextPage()
  if (nextPage) {
    return <Navigate to={`${routeBasePath}/${nextPage}`} replace />
  }

  return <>{children}</>
}

/** How often a booth re-posts its remote-printer registration. Must stay well under the server's 10-minute cutoff. */
const REMOTE_PRINTER_HEARTBEAT_MS = 3 * 60_000

/**
 * Fill in the defaults the registry expects, so every printer this booth
 * advertises carries a complete, comparable settings blob. Shared by the
 * machine-level registration and each per-printer entry.
 */
function normalizeRegisteredPrintSettings(source: PrintSettings): PrintSettings {
  return {
    ...source,
    margins: {
      marginType: source.margins?.marginType || 'default',
      ...(source.margins?.marginType === 'custom' ? source.margins : {})
    },
    dpi: source.dpi || { horizontal: 600, vertical: 600 },
    scaleFactor: source.scaleFactor || 100,
    printBackground: source.printBackground ?? true,
    printResolutionScale: source.printResolutionScale || 1
  }
}

const View: React.FC = () => {
  // `projectId` here is the raw route param — a Mongo id in kiosk, a slug in
  // digital. Use it ONLY for navigation / path matching (the slug must stay in
  // the URL). For any API call / store write / telemetry keyed by project id,
  // use `resolvedProjectId` (the real Mongo id, from the resolved config).
  const { projectId, hubId: hubRouteId } = useParams()
  const resolvedProjectId = useResolvedProjectId()
  // `/hub/:hubId` mounts this component too (HubRoute), and a hub is NOT a
  // Project: since `hubToConfig` puts the hub id in `config._id`,
  // `resolvedProjectId` resolves to it, and every project-scoped bootstrap
  // below would key on an id the `projects` collection has never held. Gated on
  // the route param, not on `hubRouteEnabled`: the route is registered
  // unconditionally, so an unflagged hub is still not a project.
  const isHubRoute = Boolean(hubRouteId)
  // `/hub/{hubId}` or `/{projectId}` — every URL this component builds hangs
  // off it, because View is mounted by HubRoute as well as ProjectRoute.
  const routeBasePath = useRouteBasePath()
  const location = useLocation()
  const { user } = useOutletContext<{ user: { _id: string; email: string; username: string } }>()
  const navigate = useNavigate()
  const {
    setDefaultProjectId,
    showCameraQualityWarnings,
    enableScreensaver,
    screensaverTimeout,
    deviceId
  } = useSettingsStore()
  // Group all useState hooks together
  const [showSettings, setShowSettings] = useState(false)
  const [isFromGearButton, setIsFromGearButton] = useState(false)
  /**
   * Which settings section to land on. Set by the paths that open settings
   * BECAUSE something specific is wrong (camera failed, camera missing at
   * startup), so the operator arrives at the fix rather than at the top of the
   * page. The gear sets nothing and lands at the top.
   */
  const [settingsSection, setSettingsSection] = useState<SettingsGroupId | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null)
  const [isTunnelActive, setIsTunnelActive] = useState<boolean>(false)
  const [machineName, setMachineName] = useState<string | null>(null)
  // null until the first build completes — distinct from an empty array, which
  // would mean "this PC genuinely has no printers".
  const [advertisedPrinters, setAdvertisedPrinters] = useState<RemotePrinterEntry[] | null>(null)
  const [showCameraPrompt, setShowCameraPrompt] = useState(false)
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false)
  const noCreditsModalTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [initialCreditsLoaded, setInitialCreditsLoaded] = useState(false)
  const [showCameraWarning, setShowCameraWarning] = useState(false)
  const [cameraQualityIssue, setCameraQualityIssue] = useState<'dark' | 'black' | 'noise' | 'none'>(
    'none'
  )
  const [isCameraWarningIgnored, setIsCameraWarningIgnored] = useState(false)
  const cameraRecoveryCallbackRef = useRef<(() => void) | null>(null)
  const reconnectFailureCount = useRef(0)
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const screensaverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [dismissedLoadingIndicator, setDismissedLoadingIndicator] = useState(false)
  const [dismissedUploadIndicator, setDismissedUploadIndicator] = useState(false)

  // Store hooks
  const { setIdle, setScreensaverActive, wakeUp, isScreensaverActive } = useIdleStore()
  const { initializeSession, logActivity, sessionUploadError, setSessionUploadError, refreshProjectCatalog } =
    useSessionStateStore()
  const sessionId = useSessionStateStore((s) => s.id)
  const { config, getPageSettings, loadingProgress, setConfig } = useConfigStore()
  const { progress: uploadProgress } = useUploadStore()
  const {
    status: printStatus,
    error: printError,
    settings,
    selectedPrinter,
    isPrintingEnabled,
    secondaryPrinter,
    tertiaryPrinter,
    setActiveProject
  } = usePrintStore()
  const { credits, fetchSubscriptionAndCredits } = useCreditsStore()
  const { isValid: isSubscriptionValid, validationError } = useSubscriptionStore()
  const { t } = useTranslation()

  // Refs
  const lastClickRef = useRef<{
    element: HTMLElement
    timestamp: Date
  } | null>(null)
  const initialDeviceValidationDone = useRef(false)

  // Get settings after store hooks
  const welcomeSettings = getPageSettings('welcome')
  const customPath = welcomeSettings?.customPath || undefined // Convert null to undefined
  const { selectedCamera } = useSettingsStore()
  // Subscribe to camera store to trigger idle timer reset when camera reconnects
  const { camera, webcam } = useCameraStore()

  // Device manager at View level so we can validate stored devices and pass to SettingsModal
  const {
    devices,
    isPreviewAvailable,
    setIsPreviewAvailable,
    localTempCamera,
    setLocalTempCamera,
    cameraConnectionRef,
    fetchDevices
  } = useDeviceManager()

  // Get page components based on customPath
  const pageComponents = useMemo(() => getCustomRoutes(customPath), [customPath])

  // Multi-project hub (docs: multi-project hub plan). The `mode-select` route
  // exists only under `/hub/:hubId` with the flag on; everywhere else the booth
  // is byte-identical to before. Requiring the hub ROUTE (not just an enabled
  // `pages.modeSelect`) keeps a Project that still carries a legacy modeSelect
  // block from opening a picker whose ids `/api/hubs` cannot resolve.
  // `hubHomePath` is where exits land while this project is a member entered
  // from a hub.
  const hubFlag = useFeatureFlag(MULTI_PROJECT_HUB_FLAG)
  /**
   * The scroll-page Booth Setup (#345/#354) ships behind a flag: allowlisted
   * accounts get the new page, everyone else keeps the modal they know. The
   * flag map is cached on the booth, so this holds offline too.
   */
  const boothSetupV2 = useFeatureFlag('booth_setup_v2')
  const hubRouteEnabled =
    hubFlag && Boolean(hubRouteId) && Boolean(config?.pages?.modeSelect?.settings?.enabled)
  const hubIdCtx = useHubStore((s) => s.hubId)
  const hubMemberIdCtx = useHubStore((s) => s.memberProjectId)
  const hubReturnToCtx = useHubStore((s) => s.returnTo)
  // On a hub route there is no `:projectId`, so home is the hub itself; on a
  // member route it is the hub the guest came from, else the project.
  const hubHomePath = hubRouteId
    ? routeBasePath
    : getHomePath(projectId, {
        hubId: hubIdCtx,
        memberProjectId: hubMemberIdCtx,
        returnTo: hubReturnToCtx
      })

  const isRootPath = useMemo(
    () => location.pathname.endsWith('/welcome') || location.pathname.endsWith(routeBasePath),
    [location.pathname, routeBasePath]
  )

  // Any page past home means a guest is mid-flow. Every flow reports it here, the
  // custom ones included (their Welcome pages start no booth session), so the
  // auto-updater never downloads or installs over a guest.
  useEffect(() => {
    reportFlowActive(!isRootPath)
    return (): void => reportFlowActive(false)
  }, [isRootPath])

  // Booth telemetry: keep the event store's project context fresh (used for
  // device incidents that fire outside an active session), and flag when the
  // session reaches a payment surface (drives the pre/post-payment split).
  // A hub is skipped rather than cleared, and the difference matters: both
  // stores treat "no id" as a reason to fall back, and neither fallback is what
  // we want here. Leaving the last member's id in place is the honest answer —
  // the booth sitting on the picker is the same physical device that just ran
  // that member.
  useEffect(() => {
    if (!isHubRoute && resolvedProjectId) {
      useBoothEventStore.getState().setProjectContext(resolvedProjectId)
    }
  }, [isHubRoute, resolvedProjectId])

  // Tell the print store which project is on screen, so it can restore the
  // remote printer remembered for it. A PC serving both karcis and photostrips
  // needs a different printer per project, and this is what makes that stick.
  // Not `setActiveProject(undefined)` on a hub: that clears `activeProjectId`,
  // and a remote print job arriving while the booth sits on the picker would
  // then lose the operator's per-project printer binding and fall back to
  // default routing.
  useEffect(() => {
    if (isHubRoute) return
    setActiveProject(resolvedProjectId || undefined)
  }, [isHubRoute, resolvedProjectId, setActiveProject])

  // Reconcile the persisted hub context with what is in the URL: back on the
  // hub, the member + auto-start ticket are dropped so the next guest starts
  // fresh; the recorded member keeps it; anything else clears it. Keyed on the
  // ROUTE params, not the resolved id — during a hub → member hop the config
  // store still holds the hub for a moment, and syncing on that would wipe the
  // ticket the member Welcome is about to consume. The two params are mutually
  // exclusive, so each call is a no-op on the other kind of route.
  useEffect(() => {
    useHubStore.getState().syncWithHub(hubRouteId)
    useHubStore.getState().syncWithProject(projectId)
  }, [hubRouteId, projectId])

  // Pre-warm each member's config + catalogue into the offline cache so the
  // pick → first page hop is instant, and survives the Wi-Fi dropping between
  // guests (§4.4). Keyed on the member id list so a config refresh that does
  // not change membership does not refetch.
  const hubMemberIdsKey = useMemo(
    () => (hubRouteEnabled ? getActiveHubMembers(config).map((m) => m.projectId).join(',') : ''),
    [hubRouteEnabled, config]
  )
  useEffect(() => {
    const hubId = config?._id
    if (!hubRouteEnabled || !hubId || !hubMemberIdsKey) return
    void prefetchHubMembers(hubId, hubMemberIdsKey.split(','))
  }, [hubRouteEnabled, hubMemberIdsKey, config?._id])
  useEffect(() => {
    const path = location.pathname
    if (path.endsWith('/checkout') || path.endsWith('/payment')) {
      useBoothEventStore.getState().markPaymentReached()
    }
  }, [location.pathname])

  const useScaleToFitShell = Boolean(customPath)
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()
  const designOrientation: DesignOrientation = deviceIsPortrait ? 'portrait' : 'landscape'
  /**
   * Custom-path sessions (incl. bahela) use a fixed design canvas. Match the device viewport
   * orientation to `screenSizes` / oriented layouts — not only `screenSize` (dashboard may
   * persist portrait while `screenSizes.landscape` is correct).
   */
  const scaleToFitScreenSize = useMemo(() => {
    if (!useScaleToFitShell || !config) return null
    return getDesignCanvasSizeForOrientation(config, designOrientation)
  }, [useScaleToFitShell, config, designOrientation])
  const scaleToFitVirtualViewport = useMemo(
    () =>
      scaleToFitScreenSize
        ? { width: scaleToFitScreenSize.width, height: scaleToFitScreenSize.height }
        : undefined,
    [scaleToFitScreenSize]
  )

  // Between sessions: re-fetch project + catalog when server version is newer than last sync.
  // Never on a hub: `HubRoute` owns that refresh against `/api/hubs/version`,
  // and during the migration window a legacy hub-Project may still answer
  // `/api/projects?id=<hubId>` — which would overwrite the projected hub config
  // with the stale project document and then fetch that project's catalogue.
  useEffect(() => {
    if (isHubRoute || !resolvedProjectId || !user || !isRootPath || sessionId) {
      return
    }

    const ac = new AbortController()

    const run = async (): Promise<void> => {
      try {
        const versionRes = await fetch(
          `${process.env.VITE_API_URL}/api/projects/version?id=${resolvedProjectId}`,
          {
            credentials: 'include',
            cache: 'no-cache',
            signal: ac.signal
          }
        )
        if (!versionRes.ok || ac.signal.aborted) {
          return
        }
        const { effectiveUpdatedAt } = (await versionRes.json()) as {
          effectiveUpdatedAt: string
        }
        if (ac.signal.aborted) {
          return
        }

        const stored = getStoredProjectEffectiveUpdatedAt(resolvedProjectId)
        if (stored === effectiveUpdatedAt) {
          return
        }

        const projRes = await fetch(
          `${process.env.VITE_API_URL}/api/projects?id=${resolvedProjectId}`,
          {
            credentials: 'include',
            cache: 'no-cache',
            signal: ac.signal
          }
        )
        if (!projRes.ok || ac.signal.aborted) {
          return
        }
        const projectData = await projRes.json()
        if (!projectData?._id || projectData._id !== resolvedProjectId || ac.signal.aborted) {
          return
        }

        useConfigStore.getState().resetLoadingProgress()
        setConfig(projectData)
        setStoredProjectEffectiveUpdatedAt(resolvedProjectId, effectiveUpdatedAt)
        await refreshProjectCatalog(resolvedProjectId)
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          return
        }
        console.warn('[VIEW] Welcome project refresh failed:', e)
      }
    }

    void run()
    return (): void => ac.abort()
  }, [isHubRoute, resolvedProjectId, user, isRootPath, sessionId, setConfig, refreshProjectCatalog])

  const uploadQueueSize = uploadProgress.pending + uploadProgress.uploading
  // Calculate effective screensaver timeout: use project's idleTimeout if set, otherwise fall back to local screensaverTimeout setting
  // Use || instead of ?? to treat 0 as falsy and ensure a minimum of 10 seconds
  const effectiveScreensaverTimeout = Math.max(config?.theme?.idleTimeout || screensaverTimeout, 10)
  const hasScreensaverVideo = Boolean(config?.theme?.screensaverVideoUrl)
  // Use project's enableScreensaver setting if defined, otherwise fall back to local setting
  const effectiveEnableScreensaver = config?.theme?.enableScreensaver ?? enableScreensaver

  // Handler for settings visibility
  const handleSettingsVisibility = (show: boolean, fromGear = false): void => {
    setShowSettings(show)
    setIsFromGearButton(fromGear)
    if (!show) {
      setIsFromGearButton(false)
      setSettingsSection(undefined)
    }
  }

  // Camera Rest (flag `camera_rest`, see docs/camera-rest-plan.md): the Welcome idle
  // action PARKS the camera — liveview off, mirror closed, USB connection KEPT — so
  // waking is gesture-free and the "Click to Start Camera" prompt never appears on the
  // idle path. This REPLACED the legacy idle-eject (full disconnect + prompt), whose
  // operator toggle ("Eject Camera When Idle (Experimental)") has been retired: idle
  // handling is now parking or nothing.
  const cameraRestEnabled = useFeatureFlag(CAMERA_REST_FLAG)

  const runIdleCameraAction = async (): Promise<void> => {
    if (unifiedCameraUtils.isAnyRecoveryActive()) {
      console.log('[VIEW] Idle timeout reached but recovery in progress — skipping')
      return
    }
    // A live preview on screen VETOES the idle park. Welcome screens are not all
    // static attract screens: several are whole single-page booths (bahela-receipt,
    // bahela-quick, the newspaper family) that render the liveview on this very
    // route, and ANY project's Welcome can place a `preview` component through the
    // Studio designer — including the default /view Welcome. A guest posing, or
    // waiting for a QRIS scan, fires no click/touch/mousemove for 60s, so "idle"
    // there means "standing still in front of the camera", not "gone". Parking
    // would freeze the preview on its last frame. Preview-less Welcome screens are
    // unaffected and still park as before.
    if (hasAnyPreviewConsumers()) {
      console.log('[VIEW] Idle timeout reached but a live preview is on screen — skipping')
      return
    }
    if (!cameraRestEnabled) return
    console.log('[VIEW] Idle timeout reached, parking camera (camera_rest)')
    try {
      await unifiedCameraUtils.parkCamera('primary')
      await unifiedCameraUtils.parkCamera('secondary')
    } catch (error) {
      console.error('[VIEW] Error parking camera on idle:', error)
    }
  }

  // Wake a parked camera on user activity. On failure (camera died while parked) fall
  // back to the legacy path: tear down and let CameraPrompt harvest the gesture for a
  // full reconnect — the worst case equals today's behavior.
  const wakeParkedCamera = (): void => {
    if (!useCameraRestStore.getState().isAnyParked()) return
    void (async (): Promise<void> => {
      const okPrimary = await unifiedCameraUtils.wakeCamera('primary')
      const okSecondary = await unifiedCameraUtils.wakeCamera('secondary')
      if (!okPrimary || !okSecondary) {
        console.warn('[VIEW] Wake from park failed, falling back to disconnect + prompt')
        if (!okPrimary) await unifiedCameraUtils.disconnect('primary', 'wake-failed')
        if (!okSecondary) await unifiedCameraUtils.disconnect('secondary', 'wake-failed')
        if (!okPrimary && !showSettings && !showCameraPrompt) {
          setShowCameraPrompt(true)
        }
      }
    })()
  }

  const handleCameraConnect = async (): Promise<void> => {

    if (selectedCamera) {
      try {
        await ensureCameraPermission()
        const { deviceType, params } = resolveConnectArgs(selectedCamera)
        const connectPromise = unifiedCameraUtils.tryToConnect(deviceType, params, 'primary')
        const timeoutPromise = new Promise((_resolve, reject) => {
          // Room for the whole self-heal cycle: attempt, then USB reset + retry.
          // + teardown/USB reset (~2.5s) + retry (<=6s). Cutting this short would abandon
          // the retry that is the difference between reconnecting and the operator having
          // to unplug the cable. The post-connect health check still catches a camera
          // that binds but is dead.
          setTimeout(() => {
            reject(new Error('Camera connection timed out'))
          }, 20000)
        })
        await Promise.race([connectPromise, timeoutPromise])

        // Verify camera actually works after connection (avoid stale/faulty session)
        const healthCheck = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheck.isResponding) {
          await unifiedCameraUtils.disconnect('primary', 'not-responding-after-connect')
          throw new Error('Camera connected but not responding')
        }

        setShowCameraPrompt(false)

        reconnectFailureCount.current = 0

        // Dual-camera booths (bahelakotak wardrobe) run BOTH cameras concurrently for the
        // whole session, so bring the secondary up at boot too. Best-effort: a failed
        // secondary must never block the booth — the capture page degrades to one pane.
        // Double-gated: the project's customPath AND the dual_camera feature flag.
        if (customPath === 'bahelakotak' && isDualCameraEnabled()) {
          // Dual runs ONLY when a secondary camera is explicitly assigned. Assigning the
          // SAME webcam as both primary and secondary is supported (e.g. one integrated
          // webcam for testing): Chromium serves one physical camera to multiple
          // getUserMedia streams within a renderer, so the secondary slot gets its own
          // independent stream/instance. No secondary assigned ⇒ normal single-camera
          // flow. (One DSLR body still can't be dual-opened: PTP/USB is single-access.)
          const secondarySelection = useSettingsStore.getState().selectedSecondaryCamera
          if (secondarySelection) {
            try {
              const secondaryArgs = resolveConnectArgs(secondarySelection)
              await Promise.race([
                unifiedCameraUtils.tryToConnect(
                  secondaryArgs.deviceType,
                  secondaryArgs.params,
                  'secondary'
                ),
                new Promise((_resolve, reject) => {
                  setTimeout(() => reject(new Error('Secondary camera connection timed out')), 5000)
                })
              ])
            } catch (secondaryError) {
              console.error('[VIEW] Secondary camera connect failed (dual mode):', secondaryError)
            }
          }
        }

        // Increment connection key to force Preview remount in Welcome page
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        // If this was a recovery reconnection, call the callback
        if (cameraRecoveryCallbackRef.current) {
          console.log('[VIEW] Camera reconnected from recovery, calling callback')
          cameraRecoveryCallbackRef.current()
          cameraRecoveryCallbackRef.current = null
        }

        // Reset idle timer immediately after successful camera reconnection
        // This handles the case where user reconnects and then leaves
        // Don't start timer if settings modal or camera prompt is open
        const currentPage = location.pathname.split('/').pop() || 'welcome'
        const isWelcomePage = isRootPath || currentPage === 'welcome'
        const screensaverHandlesIdle = effectiveEnableScreensaver && hasScreensaverVideo
        if (
          isWelcomePage &&
          cameraRestEnabled &&
          !isScreensaverActive &&
          !screensaverHandlesIdle &&
          !showSettings &&
          !showCameraPrompt
        ) {
          // Clear existing timer
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current)
            idleTimerRef.current = null
          }
          // Start new timer immediately
          console.log('[VIEW] Camera reconnected, restarting idle timer')
          idleTimerRef.current = setTimeout(async () => {
            await runIdleCameraAction()
            idleTimerRef.current = null
          }, 60000) // 60 seconds
        }
      } catch (cameraError) {
        console.error('Failed to connect to camera:', cameraError)
        const errorMessage = (cameraError as Error).message
        reconnectFailureCount.current++
        cameraLog.viewConnectFailed('primary', cameraError, {
          failuresInARow: reconnectFailureCount.current,
          recovery: !!cameraRecoveryCallbackRef.current
        })

        // If this was a recovery reconnection attempt, allow one retry then escalate
        if (cameraRecoveryCallbackRef.current) {
          if (reconnectFailureCount.current >= 2) {
            console.log('[VIEW] Recovery reconnection failed repeatedly, escalating to SettingsModal')
            cameraRecoveryCallbackRef.current = null
            reconnectFailureCount.current = 0
            const { setSelectedCamera } = useSettingsStore.getState()
            setSelectedCamera(undefined)
            setShowCameraPrompt(false)
            // Digital consumers can't open SettingsModal (no operator UI);
            // surface a toast and let the user retry / refresh.
            if (!isDigitalMode()) {
              setSettingsSection('camera')
              setShowSettings(true)
            }
            cogoToast.error(
              'Camera connection failed repeatedly. Please re-select your camera.',
              { hideAfter: 5 }
            )
          } else {
            console.log('[VIEW] Recovery reconnection failed, keeping CameraPrompt open for retry')
            cogoToast.error(`Failed to connect: ${errorMessage}. Please try again.`, { hideAfter: 5 })
          }
          return
        }

        // For non-recovery connections, if CameraPrompt fails, clear selectedCamera and show settings
        console.log('[VIEW] CameraPrompt failed, clearing selectedCamera and showing SettingsModal')
        const { setSelectedCamera } = useSettingsStore.getState()
        setSelectedCamera(undefined)
        setShowCameraPrompt(false)
        if (!isDigitalMode()) {
          setSettingsSection('camera')
          setShowSettings(true)
        }
        cogoToast.error(`Failed to connect to camera: ${errorMessage}`)
      }
    } else if (isDigitalMode()) {
      // Digital consumer: no settings modal, no camera prompt — auto-connect
      // the front camera (mobile) or browser-default (desktop) immediately.
      // See docs/digital-mode-plan.md §2.4.
      console.log('[VIEW] Digital mode: auto-connecting default webcam')
      try {
        await ensureCameraPermission()
        const ok = await connectDigitalCamera()
        if (!ok) {
          console.error('[VIEW] Digital camera auto-connect failed')
        }
      } catch (err) {
        console.error('[VIEW] Digital camera permission/connect failed:', err)
      }
      setShowCameraPrompt(false)
    } else {
      // No camera selected - close prompt and show settings
      console.log('[VIEW] No camera selected, showing SettingsModal')
      setShowCameraPrompt(false)
      setSettingsSection('camera')
      setShowSettings(true)
      // Clear recovery callback if no camera selected
      if (cameraRecoveryCallbackRef.current) {
        console.log('[VIEW] No camera selected during recovery, clearing callback')
        cameraRecoveryCallbackRef.current = null
      }
    }
  }

  // Camera recovery request handler
  const handleCameraRecoveryRequest = (onReconnected: () => void): void => {
    console.log('[VIEW] Camera recovery requested, showing CameraPrompt')
    cameraRecoveryCallbackRef.current = onReconnected
    setShowCameraPrompt(true)
  }

  // Effects
  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        // A hub owns no catalogue and no session — `HubRoute` says so and then
        // renders us anyway, so the skip has to live here. `/api/filters|frames
        // |stickers?projectId=<hubId>` match no Project, so this threw and the
        // catch below put a "Failed to fetch filters" toast on the hub Welcome,
        // once per hub → member → hub hop. The member's own `ProjectRoute`
        // initializes both when the guest picks.
        if (!isHubRoute) {
          if (!resolvedProjectId) throw new Error('No project ID provided')
          await initializeSession({ projectId: resolvedProjectId, user })
        } else {
          // The one side effect `initializeSession` had before it threw, and the
          // hub still needs it: a guest who walks out of a member mid-asset-load
          // leaves `loaded < total`, and the "Loading assets…" overlay would
          // otherwise follow them onto the hub Welcome.
          useConfigStore.getState().resetLoadingProgress()
        }
        await fetchSubscriptionAndCredits() // Fetch credits and subscription on mount

        // Send app settings telemetry on startup (lightweight ping, no GPS/PIN).
        // Kiosk-only: it's keyed to the operator's device/account, and in digital
        // the synthetic bypass user fails the server userId check (403). Skipped
        // on a hub: `projectId` is a required field there, and a hub id would
        // land in device monitoring as a project that does not exist.
        if (!isHubRoute && !isDigitalMode()) {
          import('@renderer/utils/sendAppSettingsTelemetry').then(({ sendAppSettingsTelemetry }) => {
            sendAppSettingsTelemetry({ projectId: resolvedProjectId }).catch(() => {})
          })
        }

        // Initialize asset cache cleanup
        try {
          const { useImageCacheStore } = await import('@renderer/stores/imageCacheStore')
          const imageCacheStore = useImageCacheStore.getState()

          // Clear expired entries on startup
          await imageCacheStore.clearExpired()

          // Set up periodic cleanup (every hour)
          setInterval(
            async () => {
              await imageCacheStore.clearExpired()
            },
            60 * 60 * 1000
          ) // 1 hour
        } catch (error) {
          console.warn('Error initializing asset cache cleanup:', error)
        }
      } catch (error) {
        console.error('Error initializing:', error)
        cogoToast.error(`${(error as Error).message || 'Failed to initialize application'}`, {
          hideAfter: 10
        })
      } finally {
        setIsLoading(false)
        setInitialCreditsLoaded(true)
      }
    }

    initialize()
  }, [isHubRoute, resolvedProjectId, user, initializeSession, fetchSubscriptionAndCredits])

  // Listen for camera validation requests from Welcome page
  useEffect(() => {
    const handleShowCameraPrompt = (): void => {
      if (!showSettings && !showCameraPrompt) {
        setShowCameraPrompt(true)
      }
    }

    const handleShowSettingsModal = (): void => {
      if (!showSettings) {
        setShowSettings(true)
      }
    }

    window.addEventListener('showCameraPrompt', handleShowCameraPrompt)
    window.addEventListener('showSettingsModal', handleShowSettingsModal)

    return (): void => {
      window.removeEventListener('showCameraPrompt', handleShowCameraPrompt)
      window.removeEventListener('showSettingsModal', handleShowSettingsModal)
    }
  }, [showSettings, showCameraPrompt])

  // Device validation on load: when devices are fetched, check stored camera/printer and show CameraPrompt or open SettingsModal
  useEffect(() => {
    if (!devices || initialDeviceValidationDone.current || !config) return
    const isEditCustomPath = customPath === 'edit'

    let openSettings = false

    // Restore the camera selection from durable history when it was cleared. Failure
    // and eject paths null `selectedCamera`, and this effect used to be a SILENT no-op
    // in that state — no CameraPrompt, no settings — so an unmanned booth re-entered
    // (app refresh, back-from-Projects) with the camera still physically attached
    // never offered a reconnect. Order: last-used camera if still present → 'detect'
    // if ANY PTP camera is attached → nothing (the Welcome start gate still blocks).
    let effectiveSelection = selectedCamera
    if (!effectiveSelection && !isEditCustomPath && !isDigitalMode()) {
      const { lastSelectedCamera } = useSettingsStore.getState()
      if (lastSelectedCamera && lastSelectedCamera !== 'detect') {
        const historyPresent =
          devices.cameras.webcams.some((w) => w.deviceId === lastSelectedCamera) ||
          findUsbCameraBySelectionLoose(devices.cameras.usbCameras, lastSelectedCamera) !==
            undefined
        if (historyPresent) {
          console.log('[VIEW] Restoring camera selection from history:', lastSelectedCamera)
          useSettingsStore.getState().setSelectedCamera(lastSelectedCamera)
          effectiveSelection = lastSelectedCamera
        }
      }
      if (!effectiveSelection && devices.cameras.usbCameras.length > 0) {
        console.log('[VIEW] No camera history but a USB camera is attached — using detect')
        useSettingsStore.getState().setSelectedCamera('detect')
        effectiveSelection = 'detect'
      }
    }

    // Camera: only validate if we need camera (not edit customPath)
    if (!isEditCustomPath && effectiveSelection) {
      const selectedCamera = effectiveSelection
      const webcamFound = devices.cameras.webcams.some((w) => w.deviceId === selectedCamera)
      // Loose match: at cold boot node-usb often can't read serials (and the Chromium
      // backfill is empty until the first chooser round), so an exact-serial lookup
      // would wrongly report a still-attached camera as gone and skip the prompt.
      const usbFound =
        findUsbCameraBySelectionLoose(devices.cameras.usbCameras, selectedCamera) !== undefined
      const isDetect = selectedCamera === 'detect'

      if (isDetect) {
        setShowCameraPrompt(true)
      } else if (webcamFound) {
        unifiedCameraUtils
          .tryToConnect('webcam', selectedCamera, 'primary')
          .then(() => {
            setShowCameraPrompt(false)
            const { incrementCameraConnectionKey } = useSettingsStore.getState()
            incrementCameraConnectionKey()
          })
          .catch(() => {
            cogoToast.warn(
              t('view.deviceValidation.cameraNotFound', 'Previously selected camera could not be connected.')
            )
            setSettingsSection('camera')
            setShowSettings(true)
          })
      } else if (usbFound) {
        setShowCameraPrompt(true)
      } else {
        cogoToast.warn(
          t('view.deviceValidation.cameraNotFound', 'Previously selected camera not found.')
        )
        openSettings = true
      }
    }

    // Printer: warn and open settings if printing enabled but stored printer not in list
    if (
      isPrintingEnabled &&
      selectedPrinter &&
      devices.printers?.length !== undefined &&
      !devices.printers.includes(selectedPrinter)
    ) {
      cogoToast.warn(
        t('view.deviceValidation.printerNotFound', 'Previously selected printer not found.')
      )
      openSettings = true
    }

    if (openSettings) {
      setSettingsSection('camera')
      setShowSettings(true)
    }
    initialDeviceValidationDone.current = true
  }, [devices, config, customPath, selectedCamera, selectedPrinter, isPrintingEnabled, t])

  // Digital-mode auto-connect: the consumer never picks a camera through
  // SettingsModal, so the kiosk's `selectedCamera`-driven flow above never
  // fires. Connect the default webcam (front camera on mobile, browser
  // default on desktop) once, when the config is available. See
  // docs/digital-mode-plan.md §2.4.
  const digitalAutoConnectDone = useRef(false)
  useEffect(() => {
    if (!isDigitalMode()) return
    if (!config || digitalAutoConnectDone.current) return
    const cameraStore = useCameraStore.getState()
    // Already connected (e.g. by a stage that pre-warmed) — nothing to do.
    if (cameraStore.webcam) {
      digitalAutoConnectDone.current = true
      return
    }
    digitalAutoConnectDone.current = true
    void (async (): Promise<void> => {
      try {
        await ensureCameraPermission()
        const ok = await connectDigitalCamera()
        if (!ok) {
          console.error('[VIEW] Digital camera auto-connect failed')
        }
      } catch (err) {
        console.error('[VIEW] Digital camera auto-connect error:', err)
      }
    })()
  }, [config])

  const isElectron = hasElectronPreload()

  // Track the print-server tunnel in Electron. Polled, not read once: the
  // tunnel usually comes up after this page mounts — see watchTunnelStatus.
  useEffect(() => {
    if (!isElectron) return
    return watchTunnelStatus(getTunnelStatus, (status) => {
      setTunnelUrl(status.url)
      setIsTunnelActive(status.isActive)
    })
  }, [isElectron])

  // Booth PC hostname — the group heading a remote client shows above this
  // machine's printers. Fetched once; it cannot change while the app runs.
  useEffect(() => {
    if (!isElectron) return
    void window.api
      ?.getMachineName?.()
      .then((name) => setMachineName(name ?? null))
      .catch(() => setMachineName(null))
  }, [isElectron])

  /**
   * Build the list of printers this machine advertises.
   *
   * Kept out of the heartbeat on purpose: resolving each printer's media label
   * costs an IPC round trip per printer, and re-doing that every 60 seconds to
   * republish an identical list would be pure waste. This recomputes only when
   * the installed printers or their calibration actually change.
   */
  useEffect(() => {
    if (!isElectron) return
    const names = devices?.printers
    if (!names) return

    let cancelled = false
    const build = async (): Promise<void> => {
      const entries = await Promise.all(
        names.map(async (name) => {
          // Reuse the operator's own calibration when this printer is one of the
          // booth's configured slots. Anything else is advertised without
          // settings, and the client falls back to its own defaults.
          const settingsForPrinter =
            name === selectedPrinter
              ? normalizeRegisteredPrintSettings(settings)
              : secondaryPrinter?.printerName === name
                ? normalizeRegisteredPrintSettings(secondaryPrinter.settings)
                : tertiaryPrinter?.printerName === name
                  ? normalizeRegisteredPrintSettings(tertiaryPrinter.settings)
                  : undefined

          const caps = await PrintUtils.getPrinterCapabilities(name).catch(() => null)

          return {
            name,
            isDefault: name === selectedPrinter,
            mediaLabel: describePrinterMedia(caps),
            settings: settingsForPrinter
          }
        })
      )
      if (!cancelled) setAdvertisedPrinters(entries)
    }

    void build()
    return (): void => {
      cancelled = true
    }
  }, [isElectron, devices?.printers, selectedPrinter, settings, secondaryPrinter, tertiaryPrinter])

  // Auto POST to /api/remote-printer when tunnel is active and settings/tunnelUrl change
  useEffect(() => {
    if (isElectron && tunnelUrl && isTunnelActive) {
      const postRemotePrinter = async (): Promise<void> => {
        try {
          const printSettings = normalizeRegisteredPrintSettings(settings)
          await fetch(`${process.env.VITE_API_URL}/api/remote-printer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              tunnelUrl,
              printSettings,
              projectId: resolvedProjectId,
              machineName,
              // Stable identity of THIS booth install, and the key the server
              // stores the registration under. Without it a row is keyed on
              // `tunnelUrl`, which a quick tunnel rerolls on every restart of
              // this app — so the server filed each restart as a brand-new
              // booth with a new id, and every tablet that had selected this
              // booth lost its printer ("The selected remote booth is no
              // longer registered"). Sending it lets the registration survive
              // a restart, which is what the client's print-time registry
              // refresh already assumes.
              deviceId,
              // The booth's own printer, unchanged — clients too old to read
              // `printers` still see exactly what they saw before.
              printerName: selectedPrinter || 'DS-RX1',
              // Every printer on this PC, so a remote client can pick a
              // different one. Omitted until the list has been built, so a
              // heartbeat that fires first can't publish an empty array and
              // blank the operator's picker.
              ...(advertisedPrinters ? { printers: advertisedPrinters } : {})
            })
          })
        } catch (err) {
          console.error('Failed to register remote printer from View:', err)
        }
      }
      // Register immediately, then heartbeat so an idle-but-running booth keeps its
      // registration fresh. The server prunes booths that stop checking in, so without
      // this an idle booth would disappear from the web /strip dropdown after the TTL.
      //
      // Every 3 minutes, not every minute: each beat is a server function call and a
      // database write, for every running booth, and almost no booth is ever printed
      // to remotely. The server treats a booth as gone after 10 minutes of silence,
      // so 3 minutes still survives two missed beats. A changed tunnel URL does not
      // wait for the interval — it re-runs this effect and registers at once.
      postRemotePrinter()
      const heartbeat = setInterval(postRemotePrinter, REMOTE_PRINTER_HEARTBEAT_MS)
      return (): void => clearInterval(heartbeat)
    }
    return undefined
  }, [
    isElectron,
    tunnelUrl,
    isTunnelActive,
    settings,
    selectedPrinter,
    machineName,
    advertisedPrinters,
    deviceId
  ])

  // Monitor camera status and image quality
  useEffect(() => {
    let isMonitoring = true
    // Require multiple CONSECUTIVE non-responding polls before tearing the camera down.
    // A single failed liveview read on a USB-tethered DSLR is usually a transient hiccup
    // (or momentary contention), not a disconnect — nulling on the first miss is what
    // surfaced a CameraPrompt mid-session and stranded paid customers. A genuine unplug
    // keeps failing and crosses the threshold within ~10s.
    let consecutiveNotResponding = 0
    const MONITOR_NOT_RESPONDING_THRESHOLD = 2
    const monitorCamera = async (): Promise<void> => {
      while (isMonitoring) {
        // Skip camera monitoring for edit customPath
        if (customPath === 'edit') {
          await new Promise((resolve) => setTimeout(resolve, 5000))
          continue
        }

        // For cesterstudio@gmail.com: Skip preview capture calls entirely
        // This is to test behavior without keep-alive mechanism
        const shouldSkipPreviewCapture = user?.email === 'cesterstudio@gmail.com'

        // Check both selectedCamera and actual camera/webcam state
        const cameraStore = useCameraStore.getState()
        const hasSelectedCamera = !!selectedCamera
        const hasActiveCamera = !!(cameraStore.camera || cameraStore.webcam)

        // Only monitor on pages where the camera is actively needed. Result/Edit/Filter/Select/Checkout/Payment/Frame/Retake
        // do not need a live preview — surfacing a CameraPrompt there hijacks pages like Result mid-QR-scan. Record has its
        // own dedicated preview pipeline and is intentionally excluded.
        const currentPage = location.pathname.split('/').pop() || 'welcome'
        const isCaptureRelatedPage =
          currentPage === 'welcome' ||
          currentPage === projectId || // root path /:projectId
          currentPage === 'tutorial' ||
          currentPage === 'camera' ||
          currentPage === 'capture' ||
          currentPage === 'retake' // user may re-shoot — camera still needs to be alive

        // Only monitor if we have a selected camera OR an active camera connection
        // But also check if selectedCamera exists but camera is disconnected (stale state)
        if (
          isCaptureRelatedPage &&
          (hasSelectedCamera || hasActiveCamera) &&
          !isCameraWarningIgnored && // Skip quality checks if warning is ignored
          !shouldSkipPreviewCapture && // Skip for cesterstudio@gmail.com
          // A capture page owns the camera while its recovery runs — probing/tearing
          // down here would race the reconnect it is mid-way through (double prompts,
          // store nulled under the page's probe). Stand down until it finishes.
          !unifiedCameraUtils.isAnyRecoveryActive()
        ) {
          const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')

          // Booth telemetry: feed raw responsiveness into the incident debouncer.
          // The store collapses blips (10s debounce) and measures downtime.
          useBoothEventStore.getState().setCameraConnected(qualityResult.isResponding)

          if (!qualityResult.isResponding) {
            consecutiveNotResponding += 1
            // Only clear the camera + prompt after sustained unresponsiveness — not a single blip.
            if (
              consecutiveNotResponding >= MONITOR_NOT_RESPONDING_THRESHOLD &&
              !showSettings &&
              !showCameraPrompt
            ) {
              console.log(
                `[VIEW] Camera not responding ${consecutiveNotResponding}x, clearing camera state and showing prompt`
              )
              cameraLog.disconnect('primary', 'monitor-not-responding', {
                checksInARow: consecutiveNotResponding,
                page: currentPage
              })
              useCameraStore.getState().setCamera(null)
              useCameraStore.getState().setWebcam(null)
              // Keep selectedCamera so CameraPrompt knows which camera to reconnect
              setShowCameraPrompt(true)
            }
          } else {
            // Healthy frame — reset the streak.
            consecutiveNotResponding = 0
            if (
              !qualityResult.isImageValid &&
              !showCameraWarning &&
              showCameraQualityWarnings // Only show warning if setting is enabled
            ) {
              // Show warning for image quality issues
              setCameraQualityIssue(qualityResult.qualityIssue || 'none')
              setShowCameraWarning(true)
            }
          }
        } else {
          // Not actively monitoring this tick — don't carry a stale failure streak forward.
          consecutiveNotResponding = 0
        }
        await new Promise((resolve) => setTimeout(resolve, 5000))
      }
    }

    monitorCamera()

    return (): void => {
      isMonitoring = false
    }
  }, [
    location.pathname,
    projectId,
    selectedCamera,
    showSettings,
    showCameraPrompt,
    showCameraWarning,
    isCameraWarningIgnored,
    customPath,
    user?.email,
    showCameraQualityWarnings
  ])

  // Idle detection for camera rest (park) on the welcome page
  useEffect(() => {
    const currentPage = location.pathname.split('/').pop() || 'welcome'
    const isWelcomePage = isRootPath || currentPage === 'welcome'
    const hasActiveCamera = !!(camera || webcam)

    // Only run idle detection on welcome page when a camera is connected and the
    // camera_rest flag is on. Don't run if settings modal or camera prompt is open.
    if (
      !isWelcomePage ||
      !cameraRestEnabled ||
      !hasActiveCamera ||
      showSettings ||
      showCameraPrompt ||
      isScreensaverActive ||
      (effectiveEnableScreensaver && hasScreensaverVideo)
    ) {
      // Clear timer if we're not on welcome page, feature is disabled, or modals are open
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      return
    }

    // Reset timer function
    const resetIdleTimer = (): void => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }

      // Start new 60-second timer
      idleTimerRef.current = setTimeout(async () => {
        await runIdleCameraAction()
        idleTimerRef.current = null
      }, 60000) // 60 seconds
    }

    // Start timer when entering welcome page with camera connected
    resetIdleTimer()

    // Listen for clicks to reset timer
    const handleClick = (): void => {
      // Any activity wakes a parked camera (gesture-free — the whole point of parking).
      wakeParkedCamera()
      // Only reset if still on welcome page, camera still connected, and modals are not open
      const currentCameraStore = useCameraStore.getState()
      const stillOnWelcome =
        isRootPath || (location.pathname.split('/').pop() || 'welcome') === 'welcome'
      if (
        stillOnWelcome &&
        cameraRestEnabled &&
        (currentCameraStore.camera || currentCameraStore.webcam) &&
        !showSettings &&
        !showCameraPrompt
      ) {
        resetIdleTimer()
      }
    }

    document.addEventListener('click', handleClick)
    document.addEventListener('touchstart', handleClick)
    document.addEventListener('mousemove', handleClick)
    document.addEventListener('keydown', handleClick)

    // Cleanup
    return (): void => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      document.removeEventListener('click', handleClick)
      document.removeEventListener('touchstart', handleClick)
      document.removeEventListener('mousemove', handleClick)
      document.removeEventListener('keydown', handleClick)
    }
  }, [
    location.pathname,
    isRootPath,
    cameraRestEnabled,
    selectedCamera,
    camera,
    webcam,
    showSettings,
    showCameraPrompt,
    isScreensaverActive,
    effectiveEnableScreensaver,
    hasScreensaverVideo
  ])

  // Screensaver idle detection (Welcome page only)
  useEffect(() => {
    // Debug logging
    console.log('[VIEW] Screensaver effect check:', { 
      effectiveEnableScreensaver,
      projectEnableScreensaver: config?.theme?.enableScreensaver,
      localEnableScreensaver: enableScreensaver,
      hasScreensaverVideo, 
      screensaverVideoUrl: config?.theme?.screensaverVideoUrl,
      showSettings, 
      showCameraPrompt,
      effectiveScreensaverTimeout 
    })
    
    const currentPage = location.pathname.split('/').pop() || 'welcome'
    const isWelcomePage = isRootPath || currentPage === 'welcome'

    // Only run this if on welcome page, screensaver is enabled, video exists, and settings/modals are not open
    if (
      !isWelcomePage ||
      !effectiveEnableScreensaver ||
      !hasScreensaverVideo ||
      showSettings ||
      showCameraPrompt
    ) {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      return
    }

    const resetScreensaverTimer = (): void => {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      
      const { isIdle: currentIdle } = useIdleStore.getState()
      if (currentIdle) return

      screensaverTimerRef.current = setTimeout(() => {
        console.log('[VIEW] Screensaver timeout reached, activating screensaver')
        setIdle(true)
        setScreensaverActive(true)
        void setIdleState(true)

        screensaverTimerRef.current = null
      }, effectiveScreensaverTimeout * 1000)
    }

    resetScreensaverTimer()

    const handleInteraction = (): void => {
      const { isIdle: currentIdle } = useIdleStore.getState()
      
      // Wake up first if idle, then reset timer (order matters!)
      if (currentIdle && !showSettings && !showCameraPrompt) {
        console.log('[VIEW] User interaction detected, waking up from screensaver')
        wakeUp()
        void setIdleState(false)
      }
      
      // Reset timer after wakeUp so isIdle is false and timer will be set
      resetScreensaverTimer()
    }

    document.addEventListener('click', handleInteraction)
    document.addEventListener('touchstart', handleInteraction)
    document.addEventListener('mousemove', handleInteraction)
    document.addEventListener('keydown', handleInteraction)

    return (): void => {
      if (screensaverTimerRef.current) {
        clearTimeout(screensaverTimerRef.current)
        screensaverTimerRef.current = null
      }
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
      document.removeEventListener('mousemove', handleInteraction)
      document.removeEventListener('keydown', handleInteraction)
    }
  }, [
    location.pathname,
    isRootPath,
    effectiveEnableScreensaver,
    hasScreensaverVideo,
    effectiveScreensaverTimeout,
    showSettings,
    showCameraPrompt,
    setIdle,
    setScreensaverActive,
    wakeUp
  ])

  useEffect(() => {
    const pageWhenEffectRan = location.pathname.split('/').pop() || 'welcome'
    const handleClick = (e: MouseEvent): void => {
      const element = e.target as HTMLElement
      const buttonElement = element.closest('button')
      const interactiveElement = element.closest(
        'button, a, [role="button"], [role="option"]'
      ) as HTMLElement
      // Operator panels and secret keypads (the PIN prompt) stay out of the log.
      if (interactiveElement && !isClickLogSuppressed(element)) {
        if (
          interactiveElement.hasAttribute('data-button-action') ||
          interactiveElement.hasAttribute('data-automated-click')
        ) {
          return
        }
        const timestamp = new Date()
        lastClickRef.current = {
          element: interactiveElement,
          timestamp
        }

        const actionType = interactiveElement.getAttribute('role') === 'option' ? 'change' : 'click'

        const logElement =
          buttonElement?.title || interactiveElement.getAttribute('aria-label') || undefined
        const text = interactiveElement.textContent?.trim()

        let logMessage: string
        if (actionType === 'click') {
          logMessage = `User clicked on "${logElement || text}" button`
        } else if (actionType === 'change') {
          logMessage = `User selected "${logElement || text}" option`
        } else {
          logMessage = `User performed ${actionType} on "${logElement || text} element"`
        }

        const navTo = interactiveElement.getAttribute('data-nav-to')

        logActivity(
          logMessage,
          {
            element: logElement,
            text,
            role: interactiveElement.getAttribute('role') || 'button',
            ...(navTo && { to: navTo })
          },
          navTo ? undefined : pageWhenEffectRan
        )
      }
    }

    document.addEventListener('click', handleClick)
    return (): void => document.removeEventListener('click', handleClick)
  }, [location.pathname])

  useEffect(() => {
    if (printStatus === 'error' && printError) {
      cogoToast.error(`Print error: ${printError}`)
    } else if (printStatus === 'success') {
      cogoToast.success('Print completed successfully')
    }
  }, [printStatus, printError])

  // Reset dismiss state when indicators would naturally hide so they show again next time
  useEffect(() => {
    if (loadingProgress.loaded >= loadingProgress.total && loadingProgress.total > 0) {
      setDismissedLoadingIndicator(false)
    }
  }, [loadingProgress.loaded, loadingProgress.total])
  useEffect(() => {
    if (uploadQueueSize === 0) {
      setDismissedUploadIndicator(false)
    }
  }, [uploadQueueSize])

  useEffect(() => {
    // Check if this is a Bahela project that should bypass credits
    const welcomeSettings = getPageSettings('welcome')
    const customPath = welcomeSettings?.customPath
    const isBahelaCustomPath = customPath && ['bahela-event', 'bahela'].includes(customPath)
    const isBahelaHost = window.location.hostname === 'app.bahela.id'
    const isEditCustomPath = customPath === 'edit'

  // Skip credits check for Bahela projects, hosts, edit customPath, or digital
  // mode. The public web booth is free for consumers — sessions run on the
  // project owner's account, so there is no per-visitor credit/subscription.
  if (isBahelaCustomPath || isBahelaHost || isEditCustomPath || isDigitalMode()) {
    setShowNoCreditsModal(false)
    setShowUpgradeModal(false)
    return
  }

  // If subscription is invalid and no usable credits, show NoCreditsModal immediately
  if (initialCreditsLoaded && !hasUsableBoothAccess(isSubscriptionValid, credits)) {
    setShowNoCreditsModal(true)
    return
  }

  // If device mismatch error, show UpgradeModal directly (user can change device)
    if (initialCreditsLoaded && validationError === 'device_mismatch') {
      setShowUpgradeModal(true)
      setShowNoCreditsModal(false)
      return
    }

    // For other validation errors or no subscription, show NoCreditsModal if no access
    if (initialCreditsLoaded && !hasUsableBoothAccess(isSubscriptionValid, credits)) {
      setShowNoCreditsModal(true)
      // Only show UpgradeModal if it's not a device mismatch (already handled above)
      if (validationError && validationError !== 'device_mismatch') {
        setShowUpgradeModal(false)
      }
    } else {
      if (noCreditsModalTimerRef.current) {
        clearTimeout(noCreditsModalTimerRef.current)
        noCreditsModalTimerRef.current = null
      }
      setShowNoCreditsModal(false)
      // Don't auto-close UpgradeModal - let user close it manually
    }
  }, [isSubscriptionValid, credits, initialCreditsLoaded, validationError])

  // When session upload fails with non-transient 403 (e.g. Insufficient credits), re-validate subscription so NoCreditsModal can show.
  // Surface the failure to the operator/customer via a toast + structured log; debounce per (session, reason) so retries don't spam.
  const sessionUploadToastDedupeRef = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    if (!sessionUploadError) return
    const raw = sessionUploadError
    setSessionUploadError(null)

    // Classify the underlying reason from the NON_TRANSIENT message thrown by stateUploader.
    const reason: 'insufficient_credits' | 'device_mismatch' | 'forbidden' | 'unknown' =
      /Insufficient credits/i.test(raw)
        ? 'insufficient_credits'
        : /Device not authorized/i.test(raw)
          ? 'device_mismatch'
          : /Forbidden/i.test(raw)
            ? 'forbidden'
            : 'unknown'

    console.warn('[SESSION UPLOAD] Non-transient failure', {
      reason,
      raw,
      sessionId,
      projectId
    })

    // 60s dedupe per (sessionId, reason) so a backend that keeps rejecting doesn't flood the screen.
    const dedupeKey = `${sessionId ?? '_'}|${reason}`
    const now = Date.now()
    const last = sessionUploadToastDedupeRef.current.get(dedupeKey) ?? 0
    if (now - last >= 60_000) {
      sessionUploadToastDedupeRef.current.set(dedupeKey, now)
      const message =
        reason === 'insufficient_credits'
          ? t(
              'view.sessionUploadError.insufficientCredits',
              'Session not saved: insufficient credits. Please contact the operator.'
            )
          : reason === 'device_mismatch'
            ? t(
                'view.sessionUploadError.deviceMismatch',
                'Session not saved: this device is not authorized for the active subscription.'
              )
            : t(
                'view.sessionUploadError.generic',
                'Session could not be saved. Please contact the operator.'
              )
      cogoToast.error(message, { hideAfter: 8 })
    }

    fetchSubscriptionAndCredits().catch(() => {})
  }, [sessionUploadError, setSessionUploadError, fetchSubscriptionAndCredits, sessionId, projectId, t])

  if (!config || isLoading) {
    return <Loading />
  }

  const cameraRecoveryContextValue: CameraRecoveryContextType = {
    onCameraRecoveryRequest: handleCameraRecoveryRequest
  }

  const sessionBackgroundColor = config.theme?.backgroundColor || 'white'

  const welcomeRouteElement = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <pageComponents.Welcome />
    </div>
  )

  const sessionChromeBody = (
    <>
      <div className="relative flex h-full min-h-0 w-full flex-col">
        <ControlButtons
          isRootPath={isRootPath}
          setShowSettings={handleSettingsVisibility}
          hideExitButton={Boolean(customPath)}
        />

        <div className="relative flex min-h-0 flex-1 flex-col">
          <Routes location={location}>
            <Route path="/" element={welcomeRouteElement} />
            <Route path="welcome" element={<Navigate to="/" />} />
            {/* Hub mode picker — pre-session, like tutorial, so no PageWrapper. */}
            {hubRouteEnabled && pageComponents.ModeSelect && (
              <Route
                path="mode-select"
                element={
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <pageComponents.ModeSelect />
                  </div>
                }
              />
            )}
            {pageComponents.Tutorial && (
              <Route
                path="tutorial"
                element={
                  <PageWrapper>
                    <pageComponents.Tutorial />
                  </PageWrapper>
                }
              />
            )}
            <Route
              path="camera"
              element={
                <PageWrapper>
                  <pageComponents.Camera />
                </PageWrapper>
              }
            />
            <Route
              path="payment"
              element={
                <PageWrapper>
                  <pageComponents.Payment />
                </PageWrapper>
              }
            />
            <Route
              path="filter"
              element={
                <PageWrapper>
                  <pageComponents.Filter />
                </PageWrapper>
              }
            />
            <Route
              path="frame"
              element={
                <PageWrapper>
                  <pageComponents.Frame />
                </PageWrapper>
              }
            />
            <Route
              path="capture"
              element={
                <PageWrapper>
                  <pageComponents.Capture />
                </PageWrapper>
              }
            />
            {/* Record step retired. Explicit redirect (not PageWrapper, whose flow
                logic is a no-op for custom paths — photophoria/newspaper checkouts
                still navigate here in legacy record-mode sessions): forward to
                result for record-mode sessions, select otherwise. */}
            <Route
              path="record"
              element={
                <Navigate
                  to={`${routeBasePath}/${useSessionStateStore.getState().mode === 'record' ? 'result' : 'select'}`}
                  replace
                />
              }
            />
            <Route
              path="post-session-ai"
              element={
                <PageWrapper>
                  <PostSessionAIPage />
                </PageWrapper>
              }
            />
            <Route
              path="result"
              element={
                <PageWrapper>
                  <pageComponents.Result />
                </PageWrapper>
              }
            />
            <Route
              path="retake"
              element={
                <PageWrapper>
                  <pageComponents.Retake />
                </PageWrapper>
              }
            />
            {pageComponents.Processing && (
              <Route
                path="processing"
                element={
                  <PageWrapper>
                    <pageComponents.Processing />
                  </PageWrapper>
                }
              />
            )}
            <Route
              path="select"
              element={
                <PageWrapper>
                  <pageComponents.Select />
                </PageWrapper>
              }
            />
            <Route
              path="edit"
              element={
                <PageWrapper>
                  <pageComponents.Edit />
                </PageWrapper>
              }
            />
            <Route
              path="checkout"
              element={
                <PageWrapper>
                  <pageComponents.Checkout />
                </PageWrapper>
              }
            />
            <Route path="*" element={<Navigate to={hubHomePath} replace />} />
          </Routes>
        </div>
      </div>

      {showSettings && !isDigitalMode() && boothSetupV2 && (
        <SettingsModal
          setShowSettings={handleSettingsVisibility}
          isFromGearButton={isFromGearButton}
          initialSection={settingsSection}
          height={scaleToFitScreenSize?.height ?? config.screenSize.height}
          devices={devices}
          fetchDevices={fetchDevices}
          cameraConnectionRef={cameraConnectionRef}
          isPreviewAvailable={isPreviewAvailable}
          setIsPreviewAvailable={setIsPreviewAvailable}
          localTempCamera={localTempCamera}
          setLocalTempCamera={setLocalTempCamera}
        />
      )}
      {showSettings && !isDigitalMode() && !boothSetupV2 && (
        <SettingsModalLegacy
          setShowSettings={handleSettingsVisibility}
          isFromGearButton={isFromGearButton}
          height={scaleToFitScreenSize?.height ?? config.screenSize.height}
          devices={devices}
          fetchDevices={fetchDevices}
          cameraConnectionRef={cameraConnectionRef}
          isPreviewAvailable={isPreviewAvailable}
          setIsPreviewAvailable={setIsPreviewAvailable}
          localTempCamera={localTempCamera}
          setLocalTempCamera={setLocalTempCamera}
        />
      )}
      {uploadQueueSize > 0 && !dismissedUploadIndicator && (
        <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-gray-900 bg-opacity-50 text-white text-sm pl-2 pr-1 py-1 rounded">
          <span>Uploading {uploadQueueSize} media...</span>
          <button
            type="button"
            onClick={() => setDismissedUploadIndicator(true)}
            className="p-0.5 rounded hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-1 focus:ring-white"
            aria-label={t('view.dismissUploadIndicator', 'Dismiss upload indicator')}
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>
      )}
      {loadingProgress.total > 0 &&
        loadingProgress.loaded < loadingProgress.total &&
        uploadQueueSize === 0 &&
        !dismissedLoadingIndicator && (
          <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-gray-900 bg-opacity-50 text-white text-sm pl-2 pr-1 py-1 rounded">
            <span>
              Loading assets... {loadingProgress.loaded} / {loadingProgress.total}
            </span>
            <button
              type="button"
              onClick={() => setDismissedLoadingIndicator(true)}
              className="p-0.5 rounded hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-1 focus:ring-white"
              aria-label={t('view.dismissLoadingIndicator', 'Dismiss loading indicator')}
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
        )}
      {/* Never while Settings is open. The modal is z-[200] and the prompt z-50, so the
          operator can still reach the modal — but leaving a full-screen click-catcher
          underneath it is how "I opened Settings and could not click anything" happens
          the moment the modal closes. Settings IS the place to fix a camera; the prompt
          returns on its own when it is dismissed. */}
      {showCameraPrompt && !showSettings && !isDigitalMode() && (
        <CameraPrompt onStart={handleCameraConnect} />
      )}
      {showCameraWarning && (
        <CameraWarningModal
          isVisible={showCameraWarning}
          qualityIssue={cameraQualityIssue}
          onClose={() => setShowCameraWarning(false)}
          onRetry={async () => {
            setShowCameraWarning(false)
            try {
              const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
              if (qualityResult.isResponding && qualityResult.isImageValid) {
                return
              } else if (qualityResult.isResponding && !qualityResult.isImageValid) {
                setCameraQualityIssue(qualityResult.qualityIssue || 'none')
                setShowCameraWarning(true)
              }
            } catch (error) {
              console.error('Error retrying camera check:', error)
            }
          }}
          onIgnore={() => {
            setShowCameraWarning(false)
            setIsCameraWarningIgnored(true)
          }}
        />
      )}
      {showNoCreditsModal && (
        <NoCreditsModal
          onClose={() => {
            navigate('/')
            setDefaultProjectId(undefined)
          }}
          setShowUpgradeModal={setShowUpgradeModal}
        />
      )}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      <PoweredByWatermark customPath={customPath ?? ''} />
    </>
  )

  const sessionChrome = (
    <ResponsiveTierProvider virtualViewport={scaleToFitVirtualViewport}>
      {useScaleToFitShell ? (
        <div className="relative h-full min-h-0 w-full min-w-0 flex-1 overflow-hidden bg-black">
          <ScaleToFit
            screenSize={scaleToFitScreenSize ?? config.screenSize}
            backgroundColor="white"
            blurBackgroundColor="#1D1D1D"
          >
            {sessionChromeBody}
          </ScaleToFit>
        </div>
      ) : (
        sessionChromeBody
      )}
    </ResponsiveTierProvider>
  )

  return (
    <CameraRecoveryContext.Provider value={cameraRecoveryContextValue}>
      <div className="w-screen overflow-hidden" style={{ height: '100dvh' }}>
        <div
          className="relative flex h-full min-h-0 w-full flex-col"
          style={{
            backgroundColor: useScaleToFitShell ? '#1D1D1D' : sessionBackgroundColor
          }}
        >
          {sessionChrome}
        </div>
        <Screensaver />
      </div>
    </CameraRecoveryContext.Provider>
  )
}

export default React.memo(View) // Memoize the entire View component
